import React, { useEffect, useState } from 'react';
import { Typography, Card, Row, Col, Button, Modal, List, Tag, Space, message, Badge, Input } from 'antd';
import { PhoneOutlined, CheckOutlined, RightOutlined, SyncOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import DashboardStats from '../components/Dashboard/DashboardStats';
import DashboardContent from '../components/Dashboard/DashboardContent';
import PaymentWidget from '../components/Dashboard/PaymentWidget';
import UnpaidDiagnosticsWidget from '../components/Dashboard/UnpaidDiagnosticsWidget';
import ExpiringSubscriptionsWidget from '../components/Dashboard/ExpiringSubscriptionsWidget';
import QuickActionsWidget from '../components/Dashboard/QuickActionsWidget';
import { SystemHealthCard } from '../components/Dashboard/SystemHealthCard';
import { dashboardService, QuickStats } from '../services/dashboardService';
import socketService from '../services/socketService';
import { useAuth } from '../contexts/AuthContext';

const { Title, Text } = Typography;

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const isAgent = currentUser?.role === 'agent';
  const isSuperAdmin = currentUser?.role === 'super_admin';
  const [stats, setStats] = useState<QuickStats | null>(null);
  const [callbacksModalOpen, setCallbacksModalOpen] = useState(false);
  const [pendingCallbacks, setPendingCallbacks] = useState<any[]>([]);
  const [loadingCallbacks, setLoadingCallbacks] = useState(false);
  const [updatingStatusId, setUpdatingStatusId] = useState<number | null>(null);

  // État du modal de commentaire de résolution obligatoire
  const [resolutionModalOpen, setResolutionModalOpen] = useState(false);
  const [selectedCallback, setSelectedCallback] = useState<{ id: number; name: string } | null>(null);
  const [resolutionNotes, setResolutionNotes] = useState('');
  const [submittingResolution, setSubmittingResolution] = useState(false);

  const fetchStats = async () => {
    try {
      const data = await dashboardService.getQuickStats();
      setStats(data);
    } catch (err) {
      console.error('Erreur chargement quick stats dans Dashboard header:', err);
    }
  };

  useEffect(() => {
    fetchStats();

    const handleNewCallbackRequest = (data: any) => {
      console.log('📞 Nouvelle demande de rappel capturée sur Dashboard overview:', data);
      message.info(`📞 Nouvelle demande de rappel de ${data?.name || 'Client'} (${data?.phone || ''})`);
      fetchStats();
    };

    socketService.on('new_contact_request', handleNewCallbackRequest);
    socketService.on('contact_request_created', handleNewCallbackRequest);

    return () => {
      socketService.off('new_contact_request', handleNewCallbackRequest);
      socketService.off('contact_request_created', handleNewCallbackRequest);
    };
  }, []);

  const handleOpenCallbacksModal = async () => {
    setCallbacksModalOpen(true);
    setLoadingCallbacks(true);
    try {
      const data = await dashboardService.getPendingCallbacks();
      setPendingCallbacks(data);
    } catch (e) {
      console.error('Erreur chargement des demandes de rappel:', e);
      message.error('Impossible de charger les demandes de rappel');
    } finally {
      setLoadingCallbacks(false);
    }
  };

  const handleSetInProgress = async (id: number, customerName: string) => {
    setUpdatingStatusId(id);
    try {
      await dashboardService.updateCallbackStatus(id, 'in_progress');
      message.info(`Rappel pour ${customerName} passé en cours de traitement`);
      setPendingCallbacks(prev => prev.map(c => c.id === id ? { ...c, status: 'in_progress' } : c));
    } catch (e) {
      console.error('Erreur lors du passage en cours:', e);
      message.error('Erreur lors de la mise à jour du statut');
    } finally {
      setUpdatingStatusId(null);
    }
  };

  const openResolutionModal = (id: number, customerName: string) => {
    setSelectedCallback({ id, name: customerName });
    setResolutionNotes('');
    setResolutionModalOpen(true);
  };

  const handleSubmitResolution = async () => {
    if (!selectedCallback) return;
    const trimmedNotes = resolutionNotes.trim();
    if (!trimmedNotes) {
      message.error('Le commentaire de résolution est obligatoire pour clore la demande de rappel.');
      return;
    }

    setSubmittingResolution(true);
    try {
      await dashboardService.resolveCallback(selectedCallback.id, trimmedNotes);
      message.success(`Demande de rappel pour ${selectedCallback.name} marquée comme effectuée !`);
      setPendingCallbacks(prev => prev.filter(c => c.id !== selectedCallback.id));
      if (stats) {
        setStats({
          ...stats,
          pendingContactRequests: Math.max(0, (stats.pendingContactRequests || 1) - 1)
        });
      }
      setResolutionModalOpen(false);
      setSelectedCallback(null);
      setResolutionNotes('');
    } catch (e: any) {
      console.error('Erreur lors du traitement du rappel:', e);
      message.error(e.response?.data?.message || 'Erreur lors de la clôture de la demande');
    } finally {
      setSubmittingResolution(false);
    }
  };

  const pendingCallsCount = stats?.pendingContactRequests || 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24, padding: 24 }}>
      {/* État du Système (Supervision API, BD, Redis en haut de page) — Réservé exclusivement au Super Admin */}
      {isSuperAdmin && <SystemHealthCard />}

      {/* En-tête avec Zone de Rappel Hors-horaires (Haut Droite) */}
      <Card bodyStyle={{ padding: '20px 24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16 }}>
          <div>
            <Title level={2} style={{ marginBottom: 4, marginTop: 0 }}>
              Tableau de Bord
            </Title>
            <Text type="secondary">
              Vue d'ensemble de votre activité de maintenance
            </Text>
          </div>

          {/* ZONE MARRON / ACCENT DE RAPPEL HORS-HORAIRES (TOP RIGHT) */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 16,
            backgroundColor: pendingCallsCount > 0 ? '#f0fdf4' : '#fafafa',
            border: `1.5px solid ${pendingCallsCount > 0 ? '#86efac' : '#e2e8f0'}`,
            borderRadius: 12,
            padding: '12px 20px',
            boxShadow: pendingCallsCount > 0 ? '0 4px 14px rgba(10, 84, 61, 0.15)' : 'none',
            transition: 'all 0.3s ease'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <Badge count={pendingCallsCount} overflowCount={99}>
                <div style={{
                  backgroundColor: pendingCallsCount > 0 ? '#0a543d' : '#94a3b8',
                  color: '#ffffff',
                  width: 40,
                  height: 40,
                  borderRadius: 10,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 18
                }}>
                  <PhoneOutlined />
                </div>
              </Badge>
              <div>
                <Text strong style={{ display: 'block', fontSize: 13, color: '#1e293b' }}>
                  Demandes de Rappel
                </Text>
                <Text style={{ fontSize: 12, color: pendingCallsCount > 0 ? '#15803d' : '#64748b' }}>
                  {pendingCallsCount > 0 ? `${pendingCallsCount} rappel(s) à effectuer` : 'Aucun rappel en attente'}
                </Text>
              </div>
            </div>

            <Button
              type="primary"
              icon={<PhoneOutlined />}
              onClick={handleOpenCallbacksModal}
              style={{
                backgroundColor: pendingCallsCount > 0 ? '#0a543d' : '#475569',
                borderColor: pendingCallsCount > 0 ? '#0a543d' : '#475569',
                borderRadius: 8,
                fontWeight: 600
              }}
            >
              Signaler les Rappels
            </Button>
          </div>
        </div>
      </Card>

      {/* Statistiques principales */}
      <DashboardStats />

      {/* Quick Actions & Stats + Payment Widget */}
      <Row gutter={[20, 20]}>
        <Col xs={24} lg={12}>
          <QuickActionsWidget />
        </Col>
        <Col xs={24} lg={12}>
          <UnpaidDiagnosticsWidget />
        </Col>
      </Row>

      <Row gutter={[20, 20]}>
        {!isAgent && (
          <Col xs={24} lg={12}>
            <PaymentWidget />
          </Col>
        )}
        <Col xs={24} lg={isAgent ? 24 : 12}>
          <ExpiringSubscriptionsWidget />
        </Col>
      </Row>

      {/* Contenu existant: Statistiques interventions + Activités récentes */}
      <DashboardContent />

      {/* MODAL SIGNALEMENT RAPPELS CLIENTS HORS-HORAIRES */}
      <Modal
        title={
          <Space>
            <PhoneOutlined style={{ color: '#0a543d' }} />
            <span style={{ fontWeight: 700 }}>Demandes de Rappel Client (Hors-horaires)</span>
          </Space>
        }
        open={callbacksModalOpen}
        onCancel={() => setCallbacksModalOpen(false)}
        footer={[
          <Button key="all" icon={<RightOutlined />} onClick={() => { setCallbacksModalOpen(false); navigate('/demandes-rappel'); }}>
            Voir toutes les demandes de rappel
          </Button>,
          <Button key="close" type="primary" style={{ backgroundColor: '#0a543d', borderColor: '#0a543d' }} onClick={() => setCallbacksModalOpen(false)}>
            Fermer
          </Button>
        ]}
        width={750}
      >
        <div style={{ marginBottom: 12, color: '#64748b', fontSize: 13 }}>
          Ces clients ont déposé une demande de rappel pendant la fermeture. Vous pouvez passer la demande <b>« En cours »</b> lors du traitement puis la <b>« Signaler comme Rappelé »</b> avec un commentaire obligatoire.
        </div>

        <List
          loading={loadingCallbacks}
          dataSource={pendingCallbacks}
          locale={{ emptyText: 'Aucune demande de rappel en attente' }}
          renderItem={(item: any) => {
            const customerName = item.customer 
              ? `${item.customer.first_name || ''} ${item.customer.last_name || ''}`.trim()
              : item.name || 'Client';
            const phone = item.phone || item.customer?.user?.phone || '';
            const isInProgress = item.status === 'in_progress';
            
            return (
              <List.Item
                style={{
                  padding: 16,
                  border: `1.5px solid ${isInProgress ? '#93c5fd' : '#e2e8f0'}`,
                  borderRadius: 12,
                  marginBottom: 12,
                  backgroundColor: isInProgress ? '#f0f9ff' : '#ffffff'
                }}
                actions={[
                  !isInProgress && (
                    <Button
                      type="default"
                      icon={<SyncOutlined spin={updatingStatusId === item.id} />}
                      style={{ borderRadius: 8, fontWeight: 600, borderColor: '#3b82f6', color: '#2563eb' }}
                      onClick={() => handleSetInProgress(item.id, customerName)}
                    >
                      Prendre en charge
                    </Button>
                  ),
                  <Button
                    type="primary"
                    icon={<CheckOutlined />}
                    style={{ backgroundColor: '#16a34a', borderColor: '#16a34a', borderRadius: 8, fontWeight: 600 }}
                    onClick={() => openResolutionModal(item.id, customerName)}
                  >
                    Signaler comme Rappelé
                  </Button>
                ].filter(Boolean)}
              >
                <List.Item.Meta
                  title={
                    <Space size="middle" wrap>
                      <Text strong style={{ fontSize: 15 }}>{customerName}</Text>
                      {phone && (
                        <a href={`tel:${phone.replace(/\s+/g, '')}`} style={{ color: '#0a543d', fontWeight: 600, fontSize: 13 }}>
                          📞 {phone}
                        </a>
                      )}
                      <Tag color="volcano" style={{ borderRadius: 10 }}>
                        {item.motive || 'Demande de rappel'}
                      </Tag>
                      <Tag color={isInProgress ? 'blue' : 'orange'} style={{ borderRadius: 10, fontWeight: 600 }}>
                        {isInProgress ? 'En cours de traitement' : 'En attente'}
                      </Tag>
                    </Space>
                  }
                  description={
                    <div style={{ marginTop: 6 }}>
                      <div style={{ color: '#334155', fontStyle: 'italic', marginBottom: 4 }}>
                        « {item.message || 'Souhaite être rappelé.'} »
                      </div>
                      <div style={{ fontSize: 11, color: '#94a3b8' }}>
                        Reçu le {item.created_at ? new Date(item.created_at).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' }) : 'Récemment'}
                      </div>
                    </div>
                  }
                />
              </List.Item>
            );
          }}
        />
      </Modal>

      {/* MODAL COMMENTAIRE DE RÉSOLUTION OBLIGATOIRE */}
      <Modal
        title={
          <Space>
            <CheckOutlined style={{ color: '#16a34a' }} />
            <span>Clôture du Rappel — {selectedCallback?.name}</span>
          </Space>
        }
        open={resolutionModalOpen}
        onCancel={() => {
          if (!submittingResolution) {
            setResolutionModalOpen(false);
            setSelectedCallback(null);
            setResolutionNotes('');
          }
        }}
        onOk={handleSubmitResolution}
        confirmLoading={submittingResolution}
        okText="Valider et Clore"
        cancelText="Annuler"
        okButtonProps={{ style: { backgroundColor: '#16a34a', borderColor: '#16a34a', fontWeight: 600 } }}
      >
        <div style={{ marginBottom: 12, color: '#475569', fontSize: 13 }}>
          Veuillez saisir un commentaire obligatoire décrivant le résultat de l'échange ou l'action réalisée avant de clore ce rappel :
        </div>
        <Input.TextArea
          rows={4}
          placeholder="Ex: Client rappelé à 14h. Rendez-vous pris pour un diagnostic climatiseur ce vendredi à 10h."
          value={resolutionNotes}
          onChange={(e) => setResolutionNotes(e.target.value)}
        />
      </Modal>
    </div>
  );
};

export default Dashboard;
